import {Component, OnDestroy, OnInit} from '@angular/core';
import {AccountService, AuthService} from 'auth-commons';
import {LoginService} from '../../shared/login.service';
import {I18NService} from '../../i18n/i18n.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {errorCode} from '../../commons/commons';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {CustomerConfig, Scope} from 'smag-commons/dto/api';
import {SmagApiService} from '../../api/smag-api.service';
import {switchMap} from 'rxjs/operators';
import {forkJoin, of, Subject} from 'rxjs';
import {SubjectInfo} from 'smag-commons/dto/api/subject-info';
import {EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP, ECMemberDocument} from 'smag-commons/cfg/hierarchy/ec';


@Component({
    selector: 'app-accept-clauses',
    templateUrl: './accept-clauses.component.html',
    styleUrls: ['./accept-clauses.component.scss']
})
export class AcceptClausesComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private returnUrl: string;
    readonly form: FormGroup;
    busy = false;
    privacyPolicyDocumentId: string;
    privacyPolicyLink: string;
    termsAndConditionsLink: string;
    specificClausesLink: string;
    subjectInfo: SubjectInfo;
    isAdv: boolean;

    constructor(private readonly loginService: LoginService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly fb: FormBuilder,
                private readonly accountService: AccountService,
                private readonly authService: AuthService,
                private readonly router: Router,
                private readonly route: ActivatedRoute,
                private readonly apiService: SmagApiService) {
        this.form = this.fb.group({
            termsAccepted: this.fb.control(null, this.termsAndConditions ? [Validators.requiredTrue] : []),
            privacyAccepted: this.fb.control(null, this.privacy ? [Validators.requiredTrue] : []),
            unfairAccepted: this.fb.control(null, this.unfairClauses ? [Validators.requiredTrue] : [])
        });
    }

    ngOnInit() {
        const qru = this.route.snapshot.queryParams.returnUrl; // get return url from route parameters or default to '/'
        this.returnUrl = qru && !qru.startsWith('login') && !qru.startsWith('/login') ? qru : '/';
        this.isAdv = this.authService.profile.scopes?.includes(Scope.ADVANCED);
        this.initLegalDocuments();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    acceptClauses() {
        if (this.form.valid) {
            this.busy = true;
            this.accountService.updateTerms(this.authService.profile.u3id,
                this.form.controls.termsAccepted.value,
                this.form.controls.privacyAccepted.value,
                this.form.controls.unfairAccepted.value)
                .then(login => {
                    setTimeout(() => {
                        this.busy = false;
                        this.authService.updateLogin(login, true);
                        this.loginService.notifyLogin(this.authService.profile);
                        this.router.navigate([this.returnUrl]).then();
                    }, 5000);
                })
                .catch(err => {
                    this.busy = false;
                    this.onErrorOccurred(['Wrong password', err]);
                });
        }
    }

    openECDocId(docId: string) {
        this.apiService.getECDocument(this.subjectInfo.subject.network_id, docId)
            .subscribe(([blob, filename]) => window.open(URL.createObjectURL(blob), '_blank'));
    }

    openECDocLink(link: string) {
        window.open(link, '_blank');
    }

    get termsAndConditions(): boolean {
        return !this.authService.profile.termsAccepted;
    }

    get unfairClauses(): boolean {
        return !this.authService.profile.unfairClausesAccepted;
    }

    get privacy(): boolean {
        return !this.authService.profile.privacyPolicyAccepted;
    }

    private initLegalDocuments() {
        if (this.isAdv) {
            this.apiService.getSubjectInfo().pipe(
                switchMap(subjectInfo => forkJoin([
                    this.apiService.getCustomerConfig(),
                    this.apiService
                        .getPrivacyDocument(subjectInfo.subject.network_id),
                    of(subjectInfo)]
                ))
            ).subscribe(([customerConfig, ecMemberDoc, subjectInfo]) => {
                this.subjectInfo = subjectInfo;
                this.mkLegalDocuments(customerConfig, ecMemberDoc);
            });
        } else {
            this.apiService.getCustomerConfig()
                .subscribe(customerConfig => this.mkLegalDocuments(customerConfig));
        }
    }

    private mkLegalDocuments(customerConfig: CustomerConfig, ecMemberDoc?: ECMemberDocument) {
        if (ecMemberDoc && ecMemberDoc.metadata[EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP]) {
            this.privacyPolicyDocumentId = ecMemberDoc.id;
        } else {
            this.privacyPolicyLink = customerConfig.privacy_policy_link;
        }
        this.termsAndConditionsLink = customerConfig.control_panel_terms_and_conditions_link;
        this.specificClausesLink = customerConfig.control_panel_specific_clauses_link;
    }

    private onErrorOccurred(event: [string, Error]) {
        const msg = mkErrorMessage(event[1]) || event[0];
        this.msgBoxService.error(this.i18n.translate(msg), event[1]);
    }
}

function mkErrorMessage(err: Error): string {
    // NOTE: accept-clauses call is redirected to the Authentication Server, which provides its own set of error codes
    const code = errorCode(err);
    switch (code) {
        default:
            return 'An error occurred while accepting clauses';
    }
}
